import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePrivateRooms, RoomInvitation } from './usePrivateRooms';
import { useAuth } from './useAuth';
import { toast } from '@/hooks/use-toast';

export interface UseChallengeSystem {
  challengePlayer: (
    targetUserId: string,
    targetUsername: string,
    timeControl: string,
    message?: string
  ) => Promise<{ game_session_id: string; invitation_id: string; player_color: 'white' | 'black' } | null>;
  
  pendingChallenges: RoomInvitation[];
  incomingChallenges: RoomInvitation[];
  outgoingChallenges: RoomInvitation[];
  
  acceptChallenge: (invitationId: string) => Promise<void>;
  declineChallenge: (invitationId: string) => Promise<void>;
  cancelChallenge: (invitationId: string) => Promise<void>;
  
  loading: boolean;
}

export const useChallengeSystem = (): UseChallengeSystem => {
  const [loading, setLoading] = useState(false);
  const privateRooms = usePrivateRooms();
  const { user } = useAuth();

  // Filter challenges from room invitations
  const pendingChallenges = (privateRooms?.invitations || []).filter(
    inv => inv.status === 'pending'
  );

  const incomingChallenges = user ? pendingChallenges.filter(
    inv => inv.invitee_id === user.id
  ) : [];

  const outgoingChallenges = user ? pendingChallenges.filter(
    inv => inv.inviter_id === user.id
  ) : [];

  const challengePlayer = useCallback(async (
    targetUserId: string,
    targetUsername: string,
    timeControl: string,
    message?: string
  ) => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please sign in to send challenges",
          variant: "destructive",
        });
        return null;
      }

      // Create game challenge directly (no room)
      const { data, error } = await supabase.rpc('create_direct_game_challenge', {
        _challenger_id: user.id,
        _opponent_id: targetUserId,
        _time_control: timeControl,
        _message: message || `Challenge to a ${timeControl} game!`
      });

      if (error || !data?.[0]) {
        throw new Error('Failed to create challenge');
      }

      const result = data[0];

      toast({
        title: "Challenge Sent! ⚔️",
        description: `Challenge sent to ${targetUsername}. You'll be notified when they accept.`,
      });

      return { 
        game_session_id: result.game_session_id,
        invitation_id: result.challenge_invitation_id,
        player_color: result.challenger_color as 'white' | 'black'
      };
    } catch (error) {
      console.error('Error challenging player:', error);
      toast({
        title: "Challenge Failed",
        description: "Could not send challenge. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const acceptChallenge = useCallback(async (invitationId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('room_invitations')
        .update({ status: 'accepted' })
        .eq('id', invitationId)
        .eq('invitee_id', user.id);

      if (error) throw error;

      toast({
        title: "Challenge Accepted!",
        description: "Joining the game...",
      });
    } catch (error) {
      console.error('Error accepting challenge:', error);
      toast({
        title: "Error",
        description: "Could not accept challenge",
        variant: "destructive",
      });
    }
  }, [user]);

  const declineChallenge = useCallback(async (invitationId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('room_invitations')
        .update({ status: 'declined' })
        .eq('id', invitationId)
        .eq('invitee_id', user.id);

      if (error) throw error;

      toast({
        title: "Challenge Declined",
        description: "The challenge has been declined",
      });
    } catch (error) {
      console.error('Error declining challenge:', error);
      toast({
        title: "Error",
        description: "Could not decline challenge",
        variant: "destructive",
      });
    }
  }, [user]);

  const cancelChallenge = useCallback(async (invitationId: string) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('room_invitations')
        .update({ status: 'declined' })
        .eq('id', invitationId);

      if (error) throw error;

      toast({
        title: "Challenge Cancelled",
        description: "Challenge has been cancelled",
      });
    } catch (error) {
      console.error('Error cancelling challenge:', error);
      toast({
        title: "Error",
        description: "Could not cancel challenge",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    challengePlayer,
    pendingChallenges,
    incomingChallenges,
    outgoingChallenges,
    acceptChallenge,
    declineChallenge,
    cancelChallenge,
    loading,
  };
};
